import * as THREE from 'three'
import type { MeshCoordinateOptions, MeshLoadResult, MeshUpAxis } from '@/plugins/mesh/types'

const UP_VECTORS: Readonly<Record<MeshUpAxis, THREE.Vector3>> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
}

/**
 * 校验 Mesh 坐标配置可安全用于矩阵计算。
 * @param options 待校验的可选坐标配置。
 */
export function validateMeshCoordinateOptions(options: MeshCoordinateOptions | undefined): void {
  if (!options) return
  if (!(options.sourceUpAxis in UP_VECTORS) || !(options.targetUpAxis in UP_VECTORS)) {
    throw new Error('Mesh 坐标配置包含不支持的上轴')
  }
  if (options.origin.length !== 3 || !options.origin.every(Number.isFinite)) {
    throw new Error('Mesh 坐标配置 origin 必须是有限三维坐标')
  }
}

/**
 * 构建先转换上轴、再应用目标世界原点的 Mesh 放置矩阵。
 * @param options Mesh 坐标适配配置。
 * @returns 可直接左乘 Loader 世界矩阵的放置矩阵。
 */
export function createMeshCoordinateMatrix(options: MeshCoordinateOptions): THREE.Matrix4 {
  const rotation = new THREE.Matrix4()
  if (options.sourceUpAxis !== options.targetUpAxis) {
    const quaternion = new THREE.Quaternion().setFromUnitVectors(
      UP_VECTORS[options.sourceUpAxis],
      UP_VECTORS[options.targetUpAxis]
    )
    rotation.makeRotationFromQuaternion(quaternion)
  }
  return new THREE.Matrix4()
    .makeTranslation(options.origin[0], options.origin[1], options.origin[2])
    .multiply(rotation)
}

/**
 * 将坐标配置同步应用到 Mesh 结果的对象矩阵和全部包围盒。
 * @param result Loader 返回的 Mesh 结果。
 * @param options 要应用的可选坐标配置。
 */
export function applyMeshCoordinateOptions(
  result: MeshLoadResult,
  options: MeshCoordinateOptions | undefined
): void {
  if (!options) return
  const matrix = createMeshCoordinateMatrix(options)
  const bounds = new THREE.Box3()
  for (const primitive of result.primitives) {
    primitive.object.matrix.premultiply(matrix)
    primitive.object.matrixWorld.copy(primitive.object.matrix)
    primitive.worldMatrix.premultiply(matrix)
    primitive.boundingBox.applyMatrix4(matrix)
    bounds.union(primitive.boundingBox)
  }
  result.boundingBox.copy(bounds)
  result.root.updateMatrixWorld(true)
}
